// ---------------------------------------------------------------------------
// useManagementViewFilters
//
// Single source of truth for the Management View page's filter state
// (Scope / Window / Cadence). Backed by URL query params via `useSearchParams`
// so deep-links survive refreshes and tab switches.
//
// Contract (per spec PRODOPSAI-388):
//
//  - Defaults: scope = `{ kind: 'all' }`, window = `'6m'`, cadence = `'monthly'`.
//  - Default values are NEVER written to the URL — setting a filter back to
//    its default removes the param.
//  - Invalid URL values fall back to defaults. Removal of invalid params
//    happens implicitly on the first interaction (the next setter writes a
//    URLSearchParams built from current valid state).
//  - Forward-compat: the parser accepts `?scope=product:<id>` and
//    `?scope=team:<id>` even though the FilterBar in Phase 0 only renders
//    "All P&E". This unblocks future picker UI without a URL contract change.
//
// Tab components in later phases consume this hook and MUST NOT read
// `useSearchParams` themselves — keeping URL plumbing in one place.
// ---------------------------------------------------------------------------

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  CadenceOption,
  IManagementViewFilters,
  ScopeOption,
  WindowOption,
} from '@skystone/shared';

const DEFAULT_WINDOW: WindowOption = '6m';
const DEFAULT_CADENCE: CadenceOption = 'monthly';

const VALID_WINDOWS: ReadonlySet<WindowOption> = new Set<WindowOption>(['3m', '6m', '12m']);
const VALID_CADENCES: ReadonlySet<CadenceOption> = new Set<CadenceOption>(['weekly', 'monthly']);

/** Discriminator-shape strings the URL parser recognises in `?scope=`. */
const PRODUCT_SCOPE_PREFIX = 'product:';
const TEAM_SCOPE_PREFIX = 'team:';

function parseWindow(raw: string | null): WindowOption {
  if (raw && VALID_WINDOWS.has(raw as WindowOption)) {
    return raw as WindowOption;
  }
  return DEFAULT_WINDOW;
}

function parseCadence(raw: string | null): CadenceOption {
  if (raw && VALID_CADENCES.has(raw as CadenceOption)) {
    return raw as CadenceOption;
  }
  return DEFAULT_CADENCE;
}

/**
 * Parse the `?scope=` URL value into a `ScopeOption`. Recognised shapes:
 *
 *   "all"             → { kind: 'all' }              (or unset / unknown)
 *   "product:<id>"    → { kind: 'product', productId: <id> }
 *   "team:<id>"       → { kind: 'team',    teamId:    <id> }
 *
 * Anything unrecognised falls back to `{ kind: 'all' }`.
 */
function parseScope(raw: string | null): ScopeOption {
  if (raw === null || raw === '' || raw === 'all') {
    return { kind: 'all' };
  }
  if (raw.startsWith(PRODUCT_SCOPE_PREFIX)) {
    const productId = raw.slice(PRODUCT_SCOPE_PREFIX.length);
    if (productId) return { kind: 'product', productId };
  }
  if (raw.startsWith(TEAM_SCOPE_PREFIX)) {
    const teamId = raw.slice(TEAM_SCOPE_PREFIX.length);
    if (teamId) return { kind: 'team', teamId };
  }
  return { kind: 'all' };
}

/** Serialise a `ScopeOption` back to its URL string form. Defaults → ''. */
function serialiseScope(scope: ScopeOption): string {
  if (scope.kind === 'product') return `${PRODUCT_SCOPE_PREFIX}${scope.productId}`;
  if (scope.kind === 'team') return `${TEAM_SCOPE_PREFIX}${scope.teamId}`;
  return '';
}

export interface IUseManagementViewFiltersResult extends IManagementViewFilters {
  /** PRODOPSAI-620: always materialised (never undefined) for consumers. */
  includeArchived: boolean;
  setScope: (next: ScopeOption) => void;
  setWindow: (next: WindowOption) => void;
  setCadence: (next: CadenceOption) => void;
  setIncludeArchived: (next: boolean) => void;
}

export function useManagementViewFilters(): IUseManagementViewFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const scope = useMemo(() => parseScope(searchParams.get('scope')), [searchParams]);
  const window = useMemo(() => parseWindow(searchParams.get('window')), [searchParams]);
  const cadence = useMemo(() => parseCadence(searchParams.get('cadence')), [searchParams]);
  // PRODOPSAI-620: default off; only the literal 'true' enables it (matches
  // the backend zod transform). The default is never written to the URL.
  const includeArchived = searchParams.get('includeArchived') === 'true';

  const updateParam = useCallback((key: 'scope' | 'window' | 'cadence' | 'includeArchived', value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setScope = useCallback((nextScope: ScopeOption) => {
    updateParam('scope', serialiseScope(nextScope));
  }, [updateParam]);

  const setWindow = useCallback((nextWindow: WindowOption) => {
    updateParam('window', nextWindow === DEFAULT_WINDOW ? '' : nextWindow);
  }, [updateParam]);

  const setCadence = useCallback((nextCadence: CadenceOption) => {
    updateParam('cadence', nextCadence === DEFAULT_CADENCE ? '' : nextCadence);
  }, [updateParam]);

  const setIncludeArchived = useCallback((next: boolean) => {
    updateParam('includeArchived', next ? 'true' : '');
  }, [updateParam]);

  return {
    scope,
    window,
    cadence,
    includeArchived,
    setScope,
    setWindow,
    setCadence,
    setIncludeArchived,
  };
}
